import { Button, notification, Result } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import Colors from "../../../constants/Colors";
import DataProvider from "../../../services";
import { GiCheckMark } from "react-icons/gi";
import { packageDetails } from "../../../store";

export default function ChangePlan(props: any) {
  const [api, contextHolder] = notification.useNotification();
  const [loading, setLoading] = useState<boolean>(false);
  const [subscription, setSubscription] = useState<any | null>(null);
  const [packages, setPackages] = useState<packageDetails[] | null>(null);

  const getSubscription = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: packageData }] = await Promise.all([
        DataProvider.getUserSubscription(),
        DataProvider.getAllPackages(),
      ]);
      if (data) {
        setSubscription(data);
        setPackages(packageData);
      } else {
        throw new Error("No subscription data found.");
      }
    } catch (error) {
      api["error"]({
        message: "Failed to Fetch Subscription",
        description:
          "There was an issue retrieving your subscription details. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    getSubscription();
  }, [getSubscription]);

  // The plan you're changing TO is whichever live package isn't your
  // current one - not a hardcoded id/price lookup, so this stays correct
  // if prices or package ids ever change on the backend.
  const newPackage: packageDetails | null =
    packages?.find((p) => p.id !== subscription?.mcare?.id) ?? null;

  const unsubscribed = async () => {
    try {
      const payload = {
        unsubscribeReason: "N/A",
        unsubscribeReasonList: ["Change Plan"],
      };

      setLoading(true);
      await DataProvider.unsubscribe(payload);
      props.setScreen(2);
    } catch (error) {
      api["error"]({
        message: "Failed to Change Plan",
        description: "Failed to change your plan. Kindly try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      {subscription && newPackage ? (
        <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-5 sm:gap-6">
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 bg-gradient-to-br from-[#1A94EC] to-[#0d7bc4] text-white">
                <p className="text-xs uppercase tracking-wide opacity-80">
                  Changing to
                </p>
                <h2 className="text-xl font-bold mt-1">
                  {newPackage.description}
                </h2>
                <p className="mt-2 text-2xl font-bold">
                  GH¢{newPackage.price}
                  <span className="text-sm font-normal opacity-80">
                    /month
                  </span>
                </p>
              </div>
              <div className="px-5 pb-5 pt-4">
                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">
                  What&apos;s included
                </p>
                <div className="space-y-3">
                  {newPackage.offers.map((itx, idx) => (
                    <div key={idx}>
                      <p className="text-sm font-semibold text-neutral-800 mb-1">
                        {itx.name}
                      </p>
                      <ul className="space-y-1">
                        {itx.list.map((benefit, idxx) => (
                          <li
                            key={idxx}
                            className="flex items-start gap-2 text-sm text-neutral-600"
                          >
                            <GiCheckMark
                              size={14}
                              className="text-[#1A94EC] mt-0.5 shrink-0"
                            />
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm p-1 sm:p-2">
              <Result
                status="warning"
                title="Confirm Change Plan"
                subTitle="Changing your plan will remove you completely from your current Mcare benefits. This action cannot be undone."
                extra={[
                  <Button
                    style={{ color: Colors.primaryBlue }}
                    onClick={() => props.setScreen(0)}
                    key="back"
                    className="w-full sm:w-auto"
                  >
                    Go Back
                  </Button>,
                  <Button
                    onClick={unsubscribed}
                    style={{ backgroundColor: Colors.primaryBlue }}
                    type="primary"
                    loading={loading}
                    key="confirm"
                    className="w-full sm:w-auto mt-3 sm:mt-0"
                  >
                    {loading ? "Changing..." : "I understand, Continue"}
                  </Button>,
                ]}
              />
            </div>
          </div>
        </div>
      ) : (
        <p className="text-center text-neutral-500 py-10">Loading...</p>
      )}

      {contextHolder}
    </div>
  );
}
